import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BookOpen, FileText, Mic } from "lucide-react";
import { CONFERENCES, CREDENTIALS, PUBLICATIONS } from "../data/cv";
import { fadeUp, stagger, viewportOnce } from "../lib/anim";

const TABS = [
  { id: "publications", label: "Publications", icon: FileText, testid: "tab-publications" },
  { id: "credentials", label: "Credentials", icon: BookOpen, testid: "tab-credentials" },
  { id: "conferences", label: "Conferences", icon: Mic, testid: "tab-conferences" },
];

const INDEX_STYLE = {
  PubMed: "text-sky-400 border-sky-400/30 bg-sky-400/5",
  Scopus: "text-orange-300 border-orange-300/30 bg-orange-300/5",
  EMBASE: "text-emerald-400 border-emerald-400/30 bg-emerald-400/5",
};

export default function Publications() {
  const [tab, setTab] = useState("publications");

  return (
    <section id="research" className="relative py-16 sm:py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewportOnce}>
          <motion.p variants={fadeUp} className="font-mono text-xs tracking-[0.22em] uppercase text-neon mb-4 flex items-center gap-3">
            <span className="w-8 h-px bg-gradient-to-r from-neon to-transparent" /> 04 — Research & credentials
          </motion.p>
          <motion.h2 variants={fadeUp} custom={1} className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
            Peer-reviewed research. <span className="text-grad">Current certifications.</span>
          </motion.h2>
        </motion.div>

        <div className="mt-10 flex flex-wrap gap-2" data-testid="research-tabs">
          {TABS.map(({ id, label, icon: Icon, testid }) => (
            <button
              key={id}
              data-testid={testid}
              onClick={() => setTab(id)}
              className={`relative flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                tab === id ? "text-ink" : "text-slate-400 hover:text-white border border-white/10"
              }`}
            >
              {tab === id && (
                <motion.span layoutId="tab-pill" className="absolute inset-0 rounded-full bg-gradient-to-r from-neon to-vio" transition={{ type: "spring", stiffness: 300, damping: 30 }} />
              )}
              <Icon size={14} className="relative z-10" />
              <span className="relative z-10">{label}</span>
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8"
          >
            {tab === "publications" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="publications-grid">
                {PUBLICATIONS.map((p, i) => (
                  <motion.article
                    key={p.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.06 }}
                    className="panel spot-card rounded-2xl p-6 flex flex-col hover:border-white/15 transition-colors duration-300"
                    data-testid="publication-card"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className={`text-[10px] font-mono uppercase tracking-wider border rounded-full px-2.5 py-1 ${INDEX_STYLE[p.index]}`}>
                        {p.index} indexed
                      </span>
                      <span className="font-mono text-xs text-slate-600">{p.year}</span>
                    </div>
                    <h3 className="mt-4 font-display text-base sm:text-lg font-semibold leading-snug text-slate-100">{p.title}</h3>
                    <p className="mt-2 text-xs text-slate-500 italic">{p.authors}</p>
                    <p className="mt-auto pt-4 text-xs font-mono text-slate-500">{p.journal} · {p.ref}</p>
                  </motion.article>
                ))}
              </div>
            )}

            {tab === "credentials" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="credentials-grid">
                {CREDENTIALS.map((c, i) => (
                  <motion.div
                    key={c.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, delay: i * 0.04 }}
                    className="panel spot-card rounded-xl p-5"
                    data-testid="credential-card"
                  >
                    <p className="text-sm font-semibold text-slate-100 leading-snug">{c.name}</p>
                    <p className="mt-1.5 text-xs text-slate-500">{c.org}</p>
                    <p className="mt-3 font-mono text-[11px] text-neon">{c.year}</p>
                  </motion.div>
                ))}
              </div>
            )}

            {tab === "conferences" && (
              <div className="panel rounded-2xl divide-y divide-white/[0.06]" data-testid="conferences-list">
                {CONFERENCES.map((c, i) => (
                  <motion.div
                    key={c.title}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.45, delay: i * 0.05 }}
                    className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 p-5 hover:bg-white/[0.02] transition-colors duration-300"
                    data-testid="conference-row"
                  >
                    <span className="font-mono text-xs text-vio w-14 shrink-0">{c.year}</span>
                    <p className="text-sm text-slate-200 flex-1">{c.title}</p>
                    <p className="text-xs text-slate-500">{c.venue}</p>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}